import React, { useState, useEffect } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';

function MeetingAlert() {
  const [hasAlert, setHasAlert] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [alertData, setAlertData] = useState(null);
  const [countdown, setCountdown] = useState('');
  const { darkMode } = useTheme();
  const navigate = useNavigate();

  // Simulated meeting alerts - randomly show 30 min or 10 min alerts
  useEffect(() => {
    // Initial delay before first alert (30-60 seconds for demo)
    const initialDelay = setTimeout(() => {
      triggerAlert();
    }, 30000 + Math.random() * 30000);

    return () => clearTimeout(initialDelay);
  }, []);

  // Trigger a meeting alert
  const triggerAlert = () => {
    const meetings = [
      { 
        id: 1, 
        title: 'VA EHR Review Meeting', 
        time: '2:00 PM',
        room: 'Conference Room A',
        minutesBefore: Math.random() > 0.5 ? 30 : 10 
      },
      { 
        id: 2, 
        title: 'T4NG Proposal Finalization', 
        time: '4:30 PM',
        room: 'Virtual Meeting',
        minutesBefore: Math.random() > 0.5 ? 30 : 10 
      },
      { 
        id: 3, 
        title: 'VISN 8 Stakeholder Sync', 
        time: '3:15 PM',
        room: 'Collaboration Room',
        minutesBefore: Math.random() > 0.5 ? 30 : 10 
      }
    ];

    const randomMeeting = meetings[Math.floor(Math.random() * meetings.length)];
    setAlertData(randomMeeting);
    setHasAlert(true);
    setShowPopup(false); // Don't auto-show popup, just the icon
  };

  // Countdown effect
  useEffect(() => {
    if (hasAlert && alertData) {
      let seconds = alertData.minutesBefore * 60;
      const interval = setInterval(() => {
        seconds--;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        setCountdown(`${mins}:${secs.toString().padStart(2, '0')}`);
        
        if (seconds <= 0) {
          clearInterval(interval);
        }
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [hasAlert, alertData]);

  // Schedule next alert after dismiss
  const scheduleNextAlert = () => {
    setTimeout(() => {
      triggerAlert();
    }, 90000 + Math.random() * 90000);
  };

  // Close the popup (but keep the alert icon visible)
  const handleClosePopup = () => {
    setShowPopup(false);
  };

  // Dismiss the alert completely
  const handleDismiss = () => {
    setHasAlert(false);
    setShowPopup(false);
    setAlertData(null);
    scheduleNextAlert();
  };

  const handleJoinMeeting = () => {
    setHasAlert(false);
    setShowPopup(false);
    setAlertData(null);
    navigate('/collaboration-room');
  };

  // Don't render anything if no alert or snoozed
  if (!hasAlert || !alertData) return null;

  return (
    <div className="relative">
      {/* Purple Icon Button - Compact size to match other icons */}
      <button
        onClick={() => setShowPopup(!showPopup)}
        className={`relative p-1.5 rounded-lg transition-all ${
          darkMode ? 'bg-purple-900/50 hover:bg-purple-900/70 border border-purple-500/50' : 'bg-purple-100 hover:bg-purple-200 border border-purple-300'
        }`}
        style={{ animation: 'meetingPulse 2s ease-in-out infinite' }}
      >
        {/* Calendar/Video Icon - Smaller size */}
        <svg className="text-purple-500" style={{ width: '18px', height: '18px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      </button>

      {/* Alert Popup - Only shows when clicked */}
      {showPopup && (
        <div className={`absolute right-0 top-full mt-2 w-80 rounded-xl shadow-2xl z-[100] overflow-hidden ${
          darkMode ? 'bg-gray-800 border border-purple-500/30' : 'bg-white border border-purple-200'
        }`}
        style={{ animation: 'slideDown 0.3s ease-out' }}
        >
          {/* Header */}
          <div className={`px-4 py-3 ${darkMode ? 'bg-purple-900/30' : 'bg-purple-50'} border-b ${darkMode ? 'border-purple-800/30' : 'border-purple-100'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full bg-purple-500 animate-pulse`}></div>
                <span className={`text-sm font-semibold ${darkMode ? 'text-purple-300' : 'text-purple-700'}`}>
                  Meeting Reminder
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${
                  alertData.minutesBefore <= 10 
                    ? (darkMode ? 'bg-red-900/50 text-red-300' : 'bg-red-100 text-red-700')
                    : (darkMode ? 'bg-amber-900/50 text-amber-300' : 'bg-amber-100 text-amber-700')
                }`}>
                  {alertData.minutesBefore} min
                </span>
                {/* Close Popup Button (just closes popup, doesn't dismiss) */}
                <button
                  onClick={handleClosePopup}
                  className={`p-1 rounded-lg transition-colors ${
                    darkMode ? 'hover:bg-gray-700 text-gray-400 hover:text-gray-200' : 'hover:bg-gray-200 text-gray-500 hover:text-gray-700'
                  }`}
                  title="Close popup"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4">
            <h4 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {alertData.title}
            </h4>
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {alertData.time}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  {alertData.room}
                </span>
              </div>
            </div>

            {/* Countdown Timer */}
            <div className={`mt-3 p-2 rounded-lg text-center ${
              darkMode ? 'bg-gray-700/50' : 'bg-gray-100'
            }`}>
              <span className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Starting in</span>
              <div className={`text-2xl font-mono font-bold ${
                alertData.minutesBefore <= 10 
                  ? 'text-red-500' 
                  : (darkMode ? 'text-white' : 'text-gray-900')
              }`}>
                {countdown || `${alertData.minutesBefore}:00`}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className={`px-4 py-3 flex gap-2 ${darkMode ? 'bg-gray-750 border-t border-gray-700' : 'bg-gray-50 border-t border-gray-100'}`}>
            <button
              onClick={handleDismiss}
              className={`flex-1 px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                darkMode 
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Dismiss
            </button>
            <button
              onClick={handleJoinMeeting}
              className="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-purple-500 text-white hover:bg-purple-600 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Go to Meeting
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes meetingPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
        @keyframes slideDown {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default MeetingAlert;
